import { createContext, type ReactNode, use } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PublicConfigResponse } from '@lsc/shared';
import { apiRequest } from '../api/client';
import { ErrorAlert } from '../components/ErrorAlert';
import { LoadingState } from '../components/LoadingState';

/**
 * Runtime configuration (privacy URL, Turnstile site key, base URLs) is loaded from
 * GET /api/v1/public/config – never baked into the build, so domains can change per environment.
 */
const RuntimeConfigContext = createContext<PublicConfigResponse | null>(null);

export const runtimeConfigQueryKey = ['public', 'config'] as const;

export function RuntimeConfigProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: runtimeConfigQueryKey,
    queryFn: ({ signal }) => apiRequest('publicConfig', {}, { signal }),
    staleTime: 5 * 60 * 1000,
  });
  if (query.isPending) return <LoadingState label="Wird geladen …" fullPage />;
  if (query.isError) return <ErrorAlert error={query.error} onRetry={() => void query.refetch()} />;
  return <RuntimeConfigContext value={query.data}>{children}</RuntimeConfigContext>;
}

export function useRuntimeConfig(): PublicConfigResponse {
  const config = use(RuntimeConfigContext);
  if (config === null)
    throw new Error('useRuntimeConfig must be used inside RuntimeConfigProvider');
  return config;
}
